import React, { useEffect, useRef, useState, type ReactNode } from 'react'
import { usePopper } from 'react-popper'
import type { Editor, Range } from 'slate'
import { ReactEditor } from 'slate-react'
import { ListBox } from 'shared/components/list-box'
import { Loader } from 'shared/components/loader'
import type { Suggestion } from './types'

interface MentionSuggestionsProps {
  target: Range | null
  editor: Editor
  items: Suggestion[]
  onSelect: (item: Suggestion) => void
  isLoading: boolean
}

const transformMentionsToSuggestions = (
  mentions: Suggestion[],
): {
  value: string
  label: ReactNode
}[] =>
  mentions.map(mention => ({
    value: mention.userId.toString(),
    label: (
      <div className="flex h-8 items-center justify-start px-4">
        {mention.profileImageUrl && (
          <img
            className="mr-2 size-5 rounded-full"
            width={20}
            height={20}
            src={mention.profileImageUrl}
            alt={`Avatar of ${mention.displayName}`}
          />
        )}
        <strong className="text-[15px]">{mention.displayName}</strong>
      </div>
    ),
  }))

export const MentionSuggestions = ({
  target,
  editor,
  items,
  onSelect,
  isLoading,
}: MentionSuggestionsProps): ReactNode | null => {
  const [listItems, setListItems] = useState<{ label: React.ReactNode; value: string }[]>([])

  const [referenceElement, setReferenceElement] = React.useState<HTMLDivElement | null>(null)
  const [popperElement, setPopperElement] = React.useState<HTMLDivElement | null>(null)
  const { styles: popperStyles, attributes: popperAttributes } = usePopper(
    referenceElement,
    popperElement,
    {
      placement: 'top-start',
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 20],
          },
        },
      ],
    },
  )

  const setRefElement = useRef(setReferenceElement)

  useEffect(() => {
    if (target && items.length > 0) {
      const listItems = transformMentionsToSuggestions(items)
      setListItems(listItems)
    }
  }, [target, items])

  useEffect(() => {
    if (target) {
      const el = document.createElement('div')
      document.body.append(el)
      setRefElement.current(el)
      const domRange = ReactEditor.toDOMRange(editor, target)
      const rect = domRange.getBoundingClientRect()
      el.style.position = 'absolute'
      el.style.top = `${rect.bottom + window.pageYOffset}px`
      el.style.left = `${rect.left + window.pageXOffset}px`

      return (): void => {
        el.remove()
        setRefElement.current(null)
      }
    }
  }, [target, editor])

  if (!target) return null

  return (
    <div ref={setReferenceElement}>
      <div
        ref={setPopperElement}
        className="z-50"
        style={{ ...popperStyles.popper }}
        {...popperAttributes.popper}
      >
        <div className="z-100 my-1.5 max-h-[250px] w-max max-w-[420px] min-w-[260px] gap-2.5 overflow-hidden rounded-md bg-background-secondary shadow-mention">
          {!isLoading || listItems.length > 0 ? (
            <ListBox
              options={listItems}
              onChange={userId => {
                const suggestion = items.find(item => item.userId === Number(userId))
                if (suggestion) {
                  onSelect(suggestion)
                }
              }}
            />
          ) : (
            <div className="flex items-center justify-center py-3.5">
              <Loader type="light" />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
